import { z } from "zod";
import { getAuthenticatedServerClient, schemas } from "../api/server.api";
import { applyFilter, applyPagination, applySort } from "../backoffice.utils";
import { AbstractDataProvider } from "./AbstractDataProvider";
import {
  CreateParams,
  CreateResult,
  DeleteParams,
  DeleteResult,
  GetListParams,
  GetListResult,
  GetOneParams,
  GetOneResult,
  UpdateParams,
  UpdateResult,
} from "react-admin";
import { VehicleType, VehicleTypeEnum } from "../model/VehicleType";
import { englishPlural } from "../utils/englishPlural";
import { v4 as randomUuid } from "uuid";
import _isEqual from "lodash/isEqual";
import { drivingSchoolsProvider } from "./drivingSchoolsProvider";

type VehicleDto = z.infer<typeof schemas.VehicleDto>;

export type Car = Defined<VehicleDto, "gearType"> & { type: "car" };
export type Motorcycle = VehicleDto & { type: "motorcycle" };
export type Trailer = VehicleDto & { type: "trailer" };
export type Simulator = Defined<
  VehicleDto,
  "branchId" | "minHoursInAdvance" | "maxDaysInAdvance" | "availabilities"
> & { type: "simulator" };
export type Truck = VehicleDto & { type: "truck" };
export type Bus = VehicleDto & { type: "bus" };
export type Tractor = VehicleDto & { type: "tractor" };
export type Vehicle = Car | Motorcycle | Trailer | Simulator | Truck | Bus | Tractor;

class VehiclesProvider extends AbstractDataProvider<Vehicle> {
  async getOne(resource: string, { id }: GetOneParams<Vehicle>): Promise<GetOneResult<Vehicle>> {
    const serverClient = await getAuthenticatedServerClient();
    const dto = await serverClient.getVehicle({ params: { id } });
    if (resource !== "vehicles") {
      const expectedVehicleType = _resourceToVehicleType(resource);
      if (dto.type !== expectedVehicleType) {
        throw new Error(`Vehicle ${id} is of type ${dto.type} -- expected: ${expectedVehicleType}`);
      }
    }
    return { data: _dtoToRecord(dto) };
  }

  async getList(resource: string, { pagination, sort, filter, meta }: GetListParams): Promise<GetListResult<Vehicle>> {
    const type = _resourceToVehicleType(resource);
    const { drivingSchoolId, deleted: deleted_, ...clientSideFilter } = filter;
    // Paranoia check ...
    if (Object.keys(filter).includes("deleted") && typeof deleted_ !== "boolean") {
      throw new Error(`Invalid filter.deleted: ${JSON.stringify(deleted_)} -- must be a boolean`);
    }
    const deleted = deleted_ ?? false;
    if (!drivingSchoolId) {
      throw new Error(`Unexpected filter: ${JSON.stringify(filter)} -- expected: {"drivingSchoolId": ...}`);
    }
    if (meta?.source !== "server") {
      // [PERF]: Because the Firestore driving school document has already been loaded,
      //         it's much faster to take the vehicles from there instead of another round trip to the server ...
      const drivingSchool = drivingSchoolsProvider.getOneFromCache(drivingSchoolId);
      if (drivingSchool) {
        const records = drivingSchool.vehicles;
        return applyPagination(
          applySort(applyFilter(records, { ...clientSideFilter, deleted, ...(type !== "vehicles" && { type }) }), sort),
          pagination,
        );
      }
    }
    const serverClient = await getAuthenticatedServerClient();
    const dtos = await serverClient.findVehicles({ queries: { drivingSchoolId, withDeleted: deleted } });
    const records = dtos.map(_dtoToRecord);
    return applyPagination(
      applySort(applyFilter(records, { ...clientSideFilter, deleted, ...(type !== "vehicles" && { type }) }), sort),
      pagination,
    );
  }

  async create(_resource: string, { data }: CreateParams<any>): Promise<CreateResult<Vehicle>> {
    // react-admin converts empty strings to null, we revert that transformation here ...
    if (data.notes === null) {
      data.notes = "";
    }
    const serverClient = await getAuthenticatedServerClient();
    const dto = await serverClient.createVehicle(
      schemas.CreateVehicleDto.parse({
        idempotencyKey: randomUuid(),
        // Some default values, which must be present in `CreateVehicleDto` but might not be provided by the form ...
        drivingLicenseClasses: [],
        ...data,
      }),
    );
    return { data: _dtoToRecord(dto) };
  }

  async update(_resource: string, { id, previousData, data }: UpdateParams<Vehicle>): Promise<UpdateResult<Vehicle>> {
    // react-admin converts empty strings to null, we revert that transformation here ...
    if (data.notes === null) {
      data.notes = "";
    }
    const delta = Object.fromEntries(Object.entries(data).filter(([k, v]) => !_isEqual(v, previousData[k])));
    const updateDto = schemas.UpdateVehicleDto.parse(delta);
    const serverClient = await getAuthenticatedServerClient();
    const dto = await serverClient.updateVehicle(updateDto, { params: { id } });
    return { data: _dtoToRecord(dto) };
  }

  async delete(_resource: string, { id }: DeleteParams): Promise<DeleteResult> {
    const serverClient = await getAuthenticatedServerClient();
    await serverClient.deleteVehicle(undefined, { params: { id } });
    return { data: {} };
  }
}

export const vehiclesProvider = new VehiclesProvider();

export function isSimulator(vehicle: Vehicle | undefined | null): vehicle is Simulator {
  return vehicle?.type === "simulator";
}

const _vehicleTypesByResource: Record<string, VehicleType> = Object.fromEntries(
  VehicleTypeEnum.options.map((type) => [englishPlural(type), type]),
);

function _resourceToVehicleType(resource: string): "vehicles" | VehicleType {
  if (resource === "vehicles") {
    return "vehicles";
  }
  const vehicleType = _vehicleTypesByResource[resource];
  if (!vehicleType) {
    throw new Error(
      `Unexpected resource: ${resource} -- expected one of "vehicles", "${Object.keys(_vehicleTypesByResource).join('", "')}"`,
    );
  }
  return vehicleType;
}

function _dtoToRecord(dto: VehicleDto): Vehicle {
  if (dto.type === "car") {
    if (!dto.gearType) {
      throw new Error(`Invalid VehicleDto: ${JSON.stringify(dto)} -- missing required property "gearType"`);
    }
  } else if (dto.type === "simulator") {
    if (!dto.branchId) {
      throw new Error(`Invalid VehicleDto: ${JSON.stringify(dto)} -- missing required property "branchId"`);
    } else if (typeof dto.minHoursInAdvance !== "number") {
      throw new Error(`Invalid VehicleDto: ${JSON.stringify(dto)} -- missing required property "minHoursInAdvance"`);
    } else if (typeof dto.maxDaysInAdvance !== "number") {
      throw new Error(`Invalid VehicleDto: ${JSON.stringify(dto)} -- missing required property "maxDaysInAdvance"`);
    } else if (!dto.availabilities) {
      throw new Error(`Invalid VehicleDto: ${JSON.stringify(dto)} -- missing required property "availabilities"`);
    }
  }
  return dto as Vehicle;
}
